import React, { useMemo } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useSubscriptionContext } from '../../Subscription.context';
import { useModalsContext } from '../../../ModalsProvider/Modals.context';
import { useUserContext } from '../../../UserProvider/User.context';
import { eUserRole } from '../../../UserProvider/User.enums';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useNavigate } from 'react-router-dom';
import { RouterPaths } from '../../../../components/Router/Router.constants';
import { SettingsTabs } from '../../../../pages/Settings/Settings.enum';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import './SubscriptionModals.styles.scss';

export default function PrevPastDueModal(): React.ReactElement {
  const { t } = useTranslation('settings/subscriptionsModals');
  const { subscription } = useSubscriptionContext();
  const {
    showPrevPastDueModal,
    togglePrevPastDueModal,
    toggleBankDetailsModal,
  } = useModalsContext();
  const { currentUserRole } = useUserContext();
  const navigate = useNavigate();

  const isOwner = currentUserRole === eUserRole.OWNER;
  // Bank-transfer details are a hardcoded BBVA México / MXN account, so only
  // offer that path to MXN customers (RET-1332). USD/non-MX customers must
  // never be shown that account.
  const isMxnTransferAvailable = subscription.currency?.toUpperCase() === 'MXN';
  const cancellationDays = Number(import.meta.env.VITE_CANCELLATION_DAYS) || 10;
  const cancellationDate = useMemo(
    () =>
      subscription.endDate
        ? moment(subscription.endDate).add(cancellationDays, 'days').toDate()
        : null,
    [subscription.endDate, cancellationDays],
  );

  return (
    <Modal open={showPrevPastDueModal} backdrop={false}>
      <Modal.Header className="PrevPastDueModal__header">
        {/* RET-1332: the close icon is rendered for everyone — including the
            owner — so this pre-expiry reminder is never an inescapable wall.
            Dismissal is driven by this icon (mirrors NoPaidModal): the default
            daisyui Modal backdrop is a native <form method="dialog"> with no
            React callback, so it cannot toggle the state-controlled `open`. */}
        <XMarkIcon
          onClick={() => togglePrevPastDueModal(false)}
          className="PrevPastDueModal__closeIcon"
        />
      </Modal.Header>

      <Modal.Body className="PrevPastDueModal__content">
        <p className="NoPaidModal__icon">😬</p>
        <p className="font-bold pb-4">{t('prevPastDueModal.greeting')}</p>
        <p className="text-center">
          {t('prevPastDueModal.body', {
            date: cancellationDate
              ? cancellationDate.toLocaleString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : '',
          })}
        </p>
      </Modal.Body>

      {isOwner && (
        <Modal.Actions className="PrevPastDueModal__actions">
          {/* RET-1332: primary path is updating the card on the Subscription
              tab (mirrors NoPaidModal / RET-1283), not bank transfer. */}
          <Button
            color="primary"
            onClick={() => {
              navigate(
                `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}`,
              );
              togglePrevPastDueModal(false);
            }}
          >
            {t('prevPastDueModal.primaryButton')}
          </Button>
          {isMxnTransferAvailable && (
            <Button
              color="ghost"
              onClick={() => {
                // Close this modal first so the bank-details modal isn't
                // stacked on top of it (RET-1332).
                togglePrevPastDueModal(false);
                toggleBankDetailsModal(true);
                navigate(RouterPaths.settings);
              }}
            >
              {t('prevPastDueModal.button')}
            </Button>
          )}
        </Modal.Actions>
      )}
    </Modal>
  );
}
